import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { BulkBundleCrumbs } from "../-crumbs";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  getGetBulkBundleQueryOptions,
  useCopyBulkBundleToBundle,
  useDeleteBulkBundle,
  useRestoreBulkBundle,
} from "@/requests/gen/react-query/bulk-bundle";
import { useSuspenseQuery } from "@tanstack/react-query";
import { suspenseOptions } from "@/lib/suspense-query";
import { Button } from "@/components/ui/button";
import formatUSD from "@/lib/format-currency";
import { DiscountTypeEnum } from "@/requests/gen/react-query/fastAPI.schemas";
import { resolveProductImage } from "@/lib/default-image";
import { useGetEntity } from "@/requests/gen/react-query/entity";
import { Copy, Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";
import { RequirePermission } from "@/components/universal/require-permission";
import { showAlert } from "@/lib/show-alert";
import numericId from "@/lib/numeric-id";

export const Route = createFileRoute(
  "/_private/_practice-360/bulk-bundle/$bulkBundleId/",
)({
  loader: ({ context: { queryClient }, params: { bulkBundleId } }) =>
    queryClient.ensureQueryData(
      getGetBulkBundleQueryOptions(numericId(bulkBundleId)),
    ),
  component: BulkBundleDetailsPage,
});

function BulkBundleDetailsPage() {
  const { bulkBundleId } = Route.useParams();
  const { data: bundle } = useSuspenseQuery(
    suspenseOptions(getGetBulkBundleQueryOptions(numericId(bulkBundleId))),
  );
  const { data: entity } = useGetEntity(bundle?.entity_id || 0, {
    query: { enabled: !!bundle?.entity_id },
  });
  const navigate = useNavigate();
  const copyMutation = useCopyBulkBundleToBundle();
  const deleteMutation = useDeleteBulkBundle();
  const restoreMutation = useRestoreBulkBundle();
  const isArchived = !!bundle?.is_deleted;

  const hasPatient360Service = entity?.services?.some(
    (service) => service.service === "patient_360",
  );

  const bundleTotals = bundle?.bulk_bundle_products?.reduce(
    (totals, item) => {
      const lineBasePrice = item.bulk_product.entity_price * item.quantity;
      const lineFinalPrice =
        item.discount_type === DiscountTypeEnum.Percentage
          ? lineBasePrice * (1 - item.discount_value / 100)
          : lineBasePrice - item.discount_value;

      return {
        basePrice: totals.basePrice + lineBasePrice,
        finalPrice: totals.finalPrice + lineFinalPrice,
      };
    },
    { basePrice: 0, finalPrice: 0 },
  ) ?? { basePrice: 0, finalPrice: 0 };
  const totalDiscount = bundleTotals.basePrice - bundleTotals.finalPrice;

  const handleCopyToBundle = async () => {
    if (!bundle) return;

    await copyMutation.mutateAsync(
      { bulkBundleId: bundle.bulk_bundle_id },
      {
        onSuccess: (bundleId) => {
          toast.success("Bundle Created", {
            description: "Bulk bundle successfully copied to patient bundle.",
          });
          navigate({
            to: "/bundle/$bundleId",
            params: { bundleId: String(bundleId) },
          });
        },
        onError: (error) => {
          toast.error("Error", {
            description:
              error?.response?.data?.detail?.toString() ||
              "Failed to copy bundle. Please try again.",
          });
        },
      },
    );
  };

  const showArchiveAlert = () => {
    if (!bundle) return;

    showAlert({
      title: "Archive Bundle",
      text: `Are you sure you want to archive "${bundle.name}"?`,
      img: "delete",
      cancelText: "Cancel",
      confirmText: "Archive",
      onConfirm: () => {
        if (!bundle) return;

        deleteMutation.mutate(
          { bulkBundleId: bundle.bulk_bundle_id },
          {
            onSuccess: () => {
              toast.success("Bundle archived successfully.");
              navigate({ to: "/bulk-bundle/lookup" });
            },
            onError: (error) => {
              toast.error("Error", {
                description:
                  error?.response?.data?.detail?.toString() ||
                  "Failed to archive bundle. Please try again.",
              });
            },
          },
        );
      },
    });
  };

  const showRestoreAlert = () => {
    if (!bundle) return;

    showAlert({
      title: "Restore Bundle",
      text: `Are you sure you want to restore "${bundle.name}"?`,
      img: "delete",
      cancelText: "Cancel",
      confirmText: "Restore",
      onConfirm: () => {
        if (!bundle) return;

        restoreMutation.mutate(
          { bulkBundleId: bundle.bulk_bundle_id },
          {
            onSuccess: () => {
              toast.success("Bundle restored successfully.");
              navigate({ to: "/bulk-bundle/lookup" });
            },
            onError: (error) => {
              toast.error("Error", {
                description:
                  error?.response?.data?.detail?.toString() ||
                  "Failed to restore bundle. Please try again.",
              });
            },
          },
        );
      },
    });
  };

  return (
    <div>
      <title>{`${bundle?.name || "Bulk Bundle"} - Legrande Health Portal`}</title>
      <BulkBundleCrumbs />
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle
              className={`${bundle?.is_deleted ? "text-muted-foreground" : ""}`}
            >
              {bundle?.name}
              {bundle?.is_deleted && " - Bundle Deleted"}
            </CardTitle>
            <CardDescription>{bundle?.entity.name}</CardDescription>
            <CardAction className="flex items-center gap-2">
              {bundle?.previous_bulk_bundle_id && (
                <Button asChild variant="outline" size="sm">
                  <Link
                    to="/bulk-bundle/$bulkBundleId"
                    params={{
                      bulkBundleId: String(bundle.previous_bulk_bundle_id),
                    }}
                  >
                    Previous Bundle Version
                  </Link>
                </Button>
              )}
              <RequirePermission permission="administrate_legrande">
                <div className="flex gap-2">
                  {hasPatient360Service && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopyToBundle}
                      disabled={copyMutation.isPending || isArchived}
                      loading={copyMutation.isPending}
                    >
                      <Copy />
                      Copy to Patient Bundle
                    </Button>
                  )}
                  {!isArchived ? (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={showArchiveAlert}
                      >
                        <Archive />
                        Archive
                      </Button>
                      <Button variant="outline" size="sm" asChild>
                        <Link
                          to="/bulk-bundle/$bulkBundleId/products"
                          params={{
                            bulkBundleId: String(bundle?.bulk_bundle_id),
                          }}
                        >
                          Edit
                        </Link>
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={showRestoreAlert}
                    >
                      <ArchiveRestore />
                      Restore
                    </Button>
                  )}
                </div>
              </RequirePermission>
            </CardAction>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {bundle?.bulk_bundle_products?.map((item) => {
                const basePrice = item.bulk_product.entity_price;
                let finalPrice = basePrice * item.quantity;
                if (item.discount_type === DiscountTypeEnum.Percentage) {
                  finalPrice = finalPrice * (1 - item.discount_value / 100);
                } else {
                  finalPrice = finalPrice - item.discount_value;
                }

                return (
                  <Card
                    key={item.bulk_product_id}
                    className="h-full flex flex-col"
                  >
                    <CardHeader>
                      <img
                        src={resolveProductImage(
                          item.bulk_product.catalog_product,
                        )}
                        alt={item.bulk_product.display_name}
                        className="h-32 w-full object-contain mb-4 rounded-md"
                      />
                      <CardTitle>
                        <Link
                          to="/bulk-product/$bulkProductId"
                          params={{
                            bulkProductId: String(item.bulk_product_id),
                          }}
                          className="hover:underline text-primary"
                        >
                          {item.bulk_product.display_name}
                        </Link>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="mt-auto">
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Quantity:
                          </span>
                          <span>{item.quantity}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Base Price:
                          </span>
                          <span>{formatUSD(basePrice)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Type:</span>
                          <span>{item.discount_type}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Discount:
                          </span>
                          <span>
                            {item.discount_type === DiscountTypeEnum.Percentage
                              ? `${item.discount_value}%`
                              : formatUSD(item.discount_value)}
                          </span>
                        </div>
                        <div className="flex justify-between font-bold pt-2 border-t">
                          <span>Final Price:</span>
                          <span>{formatUSD(finalPrice)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              {(!bundle?.bulk_bundle_products ||
                bundle?.bulk_bundle_products.length === 0) && (
                <div className="col-span-full text-center text-muted-foreground py-8">
                  No products in this bundle.
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-4 ">
              <div className="p-3">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  Total Price
                </p>
                <p className="text-lg font-semibold">
                  {formatUSD(bundleTotals.basePrice)}
                </p>
              </div>
              <div className="p-3">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  Total Discount
                </p>
                <p className="text-lg font-semibold">
                  {formatUSD(totalDiscount)}
                </p>
              </div>
              <div className="p-3">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  Discounted Price
                </p>
                <p className="text-lg font-semibold">
                  {formatUSD(bundleTotals.finalPrice)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
